<template>
  <TableList
    v-rect-loading="loading"
    class="site-message-list"
    row-key="id"
    :titleI18nKey="`${i18nPrefix}.menu`"
    :list-data="siteMessageList"
    :table-columns="tableColumns"
    :actionBtn="actionBtn"
    :pagination="pagination"
    @pagination-change="loadSiteMessageList(false)"
    @create="handleCreate"
  >
    <template #filter>
      <el-select
        v-model="search.companyId"
        clearable
        filterable
        popper-class="select-company-popper"
        :placeholder="$t(`${i18nPrefix}.placeholder.searchCompany`)"
        @change="loadSiteMessageList(true)"
      >
        <el-option
          v-for="(item, index) in companyOptions"
          class="company-option"
          :key="index"
          :style="{ paddingLeft: item.level * 15 + 'px' }"
          :label="item.i18nName"
          :value="item.id"
        ></el-option>
      </el-select>
      <el-input
        v-model="search.keywords"
        clearable
        :placeholder="$t(`${i18nPrefix}.placeholder.keywords`)"
        @input="handleKeywordsChange"
      ></el-input>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { isEn, getI18nPropertyValue, addI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import moment from 'moment'
import { COMPANY_STATUS } from '@/constant/company'

export default {
  components: {
    TableList
  },
  data () {
    return {
      i18nPrefix: 'admin.company.siteMessageManagement',
      loading: false,
      siteMessageList: [],
      companyOptions: [],
      actionBtn: {
        view: {
          show: true,
          onclick: this.handleEdit,
          labelI18nKey: 'admin.company.siteMessageManagement.label.view'
        }
      },
      search: {
        companyId: null,
        keywords: null
      },
      pagination: {
        total: 1,
        page: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.datetime`),
          dataKey: 'created_time',
          formatter: val => moment(val).format('YYYY-MM-DD')
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.company`),
          dataKey: 'company',
          formatter: (val, row) => this.formatCompany(row)
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.includeSubCompanies`),
          dataKey: 'is_sub_company',
          formatter: val => this.$t(`${this.i18nPrefix}.label.${val ? 'yes' : 'no'}`)
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.includeExclude`),
          dataKey: 'is_exclude',
          slotName: 'isExclude',
          formatter: val => this.$t(`${this.i18nPrefix}.label.${val ? 'excludeCompanies' : 'includeCompanies'}`)
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.messageTitle`),
          dataKey: 'title',
          slotName: 'title',
          showOverflowTooltip: true
        }
      ]
      return columns
    }
  },
  watch: {
    $route () {
      this.getCompanyFromUrl()
      this.getOrderFromUrl()
      this.handleRouteChange()
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('superAdmin', ['getSuperAdminCompanyList']),
    ...mapActions('siteMessage', ['getSiteMessageList']),
    async initPage () {
      this.loading = true
      try {
        await this.loadSiteMessageCompanyList()
        this.getCompanyFromUrl()
        this.getOrderFromUrl()
        this.handleRouteChange()
        await this.loadSiteMessageList()
      } finally {
        this.loading = false
      }
    },
    async loadSiteMessageCompanyList () {
      const resp = await this.getSuperAdminCompanyList({ status: COMPANY_STATUS.APPROVED })
      if (resp && resp.result) {
        this.companyOptions = _.chain(resp.result)
          .filter(item => !item.parent_id && !item.root_company_id)
          .flatMapDeep(root => {
            root.name = `${root.id} - ${root.name}`
            root.cn_name = `${root.id} - ${root.cn_name}`
            addI18nProperty(root, 'i18nName', 'cn_name', 'name')
            root.level = 1
            const children = this.formatCompanies(resp.result, root.id, 2)
            return [root, children]
          }).value()
      }
    },
    formatCompanies (companies, parentId, level) {
      const children = _.filter(companies, item => item.parent_id === parentId)
      return _.flatMapDeep(children, child => {
        child.name = `${child.id} - ${child.name}`
        child.cn_name = `${child.id} - ${child.cn_name}`
        addI18nProperty(child, 'i18nName', 'cn_name', 'name')
        child.level = level
        const descendants = this.formatCompanies(companies, child.id, level + 1)
        return [child, descendants]
      })
    },
    async loadSiteMessageList (reload) {
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      this.handleRouteChange(true)
      const params = {
        keywords: this.search.keywords,
        page: this.pagination.page,
        page_size: this.pagination.pageSize
      }
      if (this.search.companyId) {
        params.company_id = this.search.companyId
      }
      this.loading = true
      try {
        const resp = await this.getSiteMessageList(params)
        if (resp) {
          this.pagination.total = resp.total
          this.siteMessageList = resp.result || []
        }
      } finally {
        this.loading = false
      }
    },
    formatCompany (row) {
      if (row.is_all_company) {
        return this.$t(`${this.i18nPrefix}.label.allCompanies`)
      }
      const companyNames = _.map(row.company_names, item => getI18nPropertyValue(item, 'cn_name', 'name'))
      return _.join(companyNames, isEn() ? ', ' : '，')
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(() => this.loadSiteMessageList(true), 1000)
      }
      this.keywordsSearchDebounce()
    },
    handleCreate () {
      this.$router.push('/v-admin/client-admin/site-message-management/add')
    },
    handleEdit (row) {
      this.$router.push(`/v-admin/client-admin/site-message-management/view/${row.id}`)
    },
    getOrderFromUrl () {
      const keywords = this.$route.query.keywords
      if (keywords) {
        this.search.keywords = keywords
      }
    },
    getCompanyFromUrl () {
      const selectCompany = parseInt(this.$route.query.company)
      if (selectCompany) {
        this.search.companyId = selectCompany
      }
    },
    handleRouteChange (isPush) {
      const selectCompany = this.$route.query.company
      const keywords = this.$route.query.keywords
      if (((selectCompany || this.search.companyId) && selectCompany !== `${this.search.companyId}`) || ((keywords || this.search.keywords) && keywords !== this.search.keywords)) {
        isPush
          ? this.$router.push({ query: { company: this.search.companyId, keywords: this.search.keywords } })
          : this.$router.replace({ query: { company: this.search.companyId, keywords: this.search.keywords } })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.select-company-popper {
  .company-option {
    width: 462px;
  }
}
</style>
